/**
 * clasificadores/publicidad.js — Lógica de clasificación del dashboard de Publicidad ML
 *
 * Función pura: recibe datos crudos, devuelve secciones clasificadas.
 * Consumidores:
 *   1. PublicidadDashboard.jsx (frontend, para mostrar)
 *   2. Agente de publicidad (para ejecutar y crear tareas)
 *
 * Input:
 *   - adsItems: array de ads crudos de Firestore (mlAds)
 *   - publicaciones: array de publicaciones (mlPublicaciones)
 *   - ventas30d: array de órdenes de los últimos 30 días
 *   - campanas: array de campañas (mlCampanasAds) — para ACOS estratégico
 *   - config: { acosEstrategico?: number } — ACOS estratégico a nivel empresa
 *
 * Output:
 *   {
 *     analisis: { acosPromedio, volumenPromedio, ctrRef, cvrRef, cuadrantes, todos },
 *     secciones: { activar, rendimiento, pausar, optimizar, revisar, potencial, generadoresIndirectos },
 *     kpis: { activar: {...}, rendimiento: {...}, pausar: {...}, optimizar: {...}, revisar: {...} },
 *     costePausar: number,
 *   }
 */

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

import { esAdVivo } from '@modules/mercadolibre/lib/promoCanonicoShape'
import { ventanaDeVentas, escalarADias, ahorroDeGastoDeAd, FUENTE_VENTANA } from './ventanaDeVentas'
// 10-jul-2026 — paridad manual↔automático: los mismos criterios que aplica
// PublicidadDashboard tienen que regir acá (el agente NO puede sugerir lo que
// la vista manual ya no sugiere).
import { computarCuarentena } from '../bitacora'
import { gateAdsPrender, construirInfoRentabilidadPorItem } from './gatePromoRentabilidad'
import { getPisoClicks, getUmbralAcosGeneradores } from '../umbralesPublicidad'
import { causaMargenNegativo, gastoSinAtribuirDePublicacion } from './causaMargenNegativo'

// C6g: el status del ad es intradía-volátil (hold/idle = vivo, ML-validado
// C6b) — filtrar por `status === 'active'` contra la canónica da 0.
// "Operativo" = vivo y NO pausado a propósito: el espejo robusto de la
// intención original de los filtros 'active' de las secciones.
const esAdOperativo = (status) => esAdVivo(status) && status !== 'paused'

/** Enriquecer ads con datos de la publicación */
function enriquecerAds(adsItems, publicaciones) {
  const pubsMap = {}
  for (const p of publicaciones) pubsMap[p.id] = p

  return adsItems.map(ad => {
    const itemId = ad.item_id || ad.id?.split('_').pop()
    const pub = pubsMap[itemId]
    const m = ad.metrics || ad
    // Rollup Capa 2 persistido en la publicación (rentabilidadInferida →
    // _rentabilidadInferida en el shape legacy). Alimenta la sección
    // pausarRent — misma fuente que PublicidadDashboard (10-jul-2026).
    const rentInferida = pub?._rentabilidadInferida || null
    return {
      ...ad,
      _itemId: itemId,
      _rentInferida:    rentInferida,
      _margenPct:       rentInferida?.margenPctProm ?? null,         // con publicidad
      _margenSinPubPct: rentInferida?.margenSinPubPctProm ?? null,   // sin publicidad
      // report UQndSudKpBhxAxwjYoLE — ver causaMargenNegativo.js.
      _gastoSinAtribuir: gastoSinAtribuirDePublicacion(rentInferida, pub?._costoAdquisicionInferido),
      _titulo: pub?.title || ad.title || itemId || '—',
      _thumbnail: pub?.thumbnail || pub?.pictures?.[0]?.url || null,
      _precio: pub?.price || ad.price || null,
      _status: pub?.status || null,
      _stock: (pub?.availableQuantity ?? pub?.available_quantity) ?? ad.available_quantity ?? null,
      // Flywheel (14-jul-2026): pub EN PROMO → el ad pausado NO se reactiva
      // (jugada de Central Promos: la publi se come el margen de la promo;
      // se levanta sola cuando la promo termina).
      _enPromo: Array.isArray(pub?.dealIds) && pub.dealIds.length > 0,
      _permalink: pub?.permalink || (itemId ? `https://articulo.mercadolibre.com.ar/${itemId.replace(/^(MLA)(\d+)$/, '$1-$2')}` : null),
      _clicks: m.clicks || 0,
      _prints: m.prints || 0,
      _cost: m.cost || 0,
      _ctr: m.prints > 0 ? (m.clicks || 0) / m.prints : 0,
      _cpc: m.clicks > 0 ? (m.cost || 0) / (m.clicks || 1) : 0,
      _units: m.units_quantity || 0,
      _amount: m.total_amount || 0,
      _roas: m.cost > 0 ? (m.total_amount || 0) / m.cost : 0,
      _organicUnits: m.organic_units_quantity || 0,
      _directAmount: m.direct_amount || 0,
      _indirectAmount: m.indirect_amount || 0,
      _directUnits: m.direct_units_quantity || 0,
      _indirectUnits: m.indirect_units_quantity || 0,
      _indirectItems: m.indirect_items_quantity || 0,
      _acosDirecto: (m.direct_amount || 0) > 0 ? (m.cost || 0) / (m.direct_amount || 1) : null,
      _acosTotal: (m.total_amount || 0) > 0 ? (m.cost || 0) / (m.total_amount || 1) : null,
      _esGeneradorIndirecto: (m.indirect_amount || 0) > 0,
      _pctIndirecto: (m.total_amount || 0) > 0 ? (m.indirect_amount || 0) / m.total_amount : 0,
    }
  }).sort((a, b) => b._cost - a._cost)
}

/** Armar mapa de ventas por item (últimos 30 días) */
function buildVentasPorItem(ventas30d) {
  const m = {}
  for (const orden of ventas30d) {
    for (const oi of (orden.order_items || [])) {
      const itemId = oi.item?.id
      if (!itemId) continue
      if (!m[itemId]) m[itemId] = { uds: 0, monto: 0 }
      m[itemId].uds += Number(oi.quantity || 0)
      m[itemId].monto += Number(oi.quantity || 0) * Number(oi.unit_price || 0)
    }
  }
  return m
}

/** KPIs de un grupo de ads */
function calcKpis(items) {
  const inv = items.reduce((s, a) => s + (a._cost || 0), 0)
  const fac = items.reduce((s, a) => s + (a._amount || 0), 0)
  const uds = items.reduce((s, a) => s + (a._units || 0), 0)
  const clk = items.reduce((s, a) => s + (a._clicks || 0), 0)
  const imp = items.reduce((s, a) => s + (a._prints || 0), 0)
  const perdida = items.reduce((s, a) => {
    const c = a._cost || 0, am = a._amount || 0
    if (am === 0 && c > 0) return s + c
    if (c > am && am > 0) return s + (c - am)
    return s
  }, 0)
  return {
    inv, fac, uds, clk, imp, perdida,
    acos: fac > 0 ? inv / fac : 0,
    roas: inv > 0 ? fac / inv : 0,
    ctr: imp > 0 ? clk / imp : 0,
    cpc: clk > 0 ? inv / clk : 0,
    cvr: clk > 0 ? uds / clk : 0,
  }
}

// ═══════════════════════════════════════════════════════════════
// Análisis de eficiencia (matriz ACOS × Volumen)
// ═══════════════════════════════════════════════════════════════

function analizarEficiencia(adsEnriquecidos, campanas, config) {
  const conGasto = adsEnriquecidos.filter(a => a._cost > 0)
  const conVentas = conGasto.filter(a => a._amount > 0)
  const sinVentas = conGasto.filter(a => a._amount === 0)

  const totalClicksGlobal = conGasto.reduce((s, a) => s + a._clicks, 0)
  const totalPrintsGlobal = conGasto.reduce((s, a) => s + a._prints, 0)
  const ctrGlobal = totalPrintsGlobal > 0 ? totalClicksGlobal / totalPrintsGlobal : 0

  if (conVentas.length === 0) {
    const perdidas = sinVentas.map(a => {
      const accion = clasificarAccionSinVentas(a, ctrGlobal)
      return { ...a, _cuadrante: 'perdida', _acosCalc: null, ...accion }
    })
    return {
      acosPromedio: 0, volumenPromedio: 0, ctrRef: ctrGlobal, cvrRef: 0,
      cuadrantes: { estrella: [], oportunidad: [], tanque: [], pozo: [], estable: [], perdida: perdidas },
      todos: perdidas,
    }
  }

  const totalCost = conVentas.reduce((s, a) => s + a._cost, 0)
  const totalAmount = conVentas.reduce((s, a) => s + a._amount, 0)
  const acosPromedio = totalAmount > 0 ? totalCost / totalAmount : 0
  const volumenPromedio = totalAmount / conVentas.length

  // ACOS estratégico por campaña
  const campAcosMap = {}
  for (const c of (campanas || [])) {
    const cid = c.campaign_id || c.id
    if (c.acosEstrategico != null) campAcosMap[cid] = c.acosEstrategico
  }
  const acosEstrategicoGeneral = config?.acosEstrategico ?? null

  const resolveAcosRef = (ad) => {
    if (ad.acosEstrategico != null) return ad.acosEstrategico
    const campId = ad.campaign_id
    if (campId && campAcosMap[campId] != null) return campAcosMap[campId]
    if (acosEstrategicoGeneral != null) return acosEstrategicoGeneral
    return acosPromedio
  }

  // Pasada 1: clasificar cuadrantes
  const cuadrantesPre = { estrella: [], oportunidad: [], tanque: [], pozo: [], estable: [], perdida: [] }

  for (const ad of conVentas) {
    const acos = ad._amount > 0 ? ad._cost / ad._amount : 999
    const vol = ad._amount
    const adAcosRef = resolveAcosRef(ad)
    const adAcosAlto = adAcosRef * 1.15
    const adAcosBajo = adAcosRef * 0.85
    const volAlto = volumenPromedio * 1.15
    const volBajo = volumenPromedio * 0.85

    let cuadrante
    if (acos <= adAcosBajo && vol >= volAlto) cuadrante = 'estrella'
    else if (acos <= adAcosBajo && vol < volBajo) cuadrante = 'oportunidad'
    else if (acos >= adAcosAlto && vol >= volAlto) cuadrante = 'tanque'
    else if (acos >= adAcosAlto && vol < volBajo) cuadrante = 'pozo'
    else cuadrante = 'estable'

    cuadrantesPre[cuadrante].push({ ...ad, _cuadrante: cuadrante, _acosCalc: acos, _acosRef: adAcosRef })
  }

  // Separar estables
  const acosLimiteEstable = acosPromedio * 1.10
  const estableBuenos = cuadrantesPre.estable.filter(a => a._acosCalc <= acosLimiteEstable)

  // CTR y CVR de referencia
  const buenos = [...cuadrantesPre.estrella, ...cuadrantesPre.oportunidad, ...estableBuenos]
  const refClicks = buenos.reduce((s, a) => s + a._clicks, 0)
  const refPrints = buenos.reduce((s, a) => s + a._prints, 0)
  const ctrRef = refPrints > 0 ? refClicks / refPrints : ctrGlobal
  const refUnits = buenos.reduce((s, a) => s + a._units, 0)
  const cvrRef = refClicks > 0 ? refUnits / refClicks : 0

  // Pasada 2: asignar acciones
  const cuadrantes = { estrella: [], oportunidad: [], estable: [], tanque: [], pozo: [], perdida: [] }

  for (const ad of buenos) {
    cuadrantes[ad._cuadrante].push({ ...ad, _accion: null, _motivo: null, _prioridad: 0 })
  }

  // Tanque
  for (const ad of cuadrantesPre.tanque) {
    const _prioridad = (ad._clicks || 0) * (ad._precio || 0)
    cuadrantes.tanque.push({
      ...ad, _accion: 'revisar',
      _motivo: 'Vende pero caro. Optimizá keywords, bids o el listing para bajar el ACOS.',
      _prioridad,
    })
  }

  // Pozo
  for (const ad of cuadrantesPre.pozo) {
    const ctr = ad._ctr || 0
    const clicks = ad._clicks || 0
    const precio = ad._precio || 0
    const status = ad.status || ''
    const yaPausada = status === 'paused' || status === 'stopped'
    const buenCtr = ctr >= ctrRef
    const _prioridad = clicks * precio
    const ctrTxt = `CTR ${(ctr * 100).toFixed(2)}% (ref: ${(ctrRef * 100).toFixed(2)}%)`

    if (yaPausada && buenCtr) {
      cuadrantes.pozo.push({ ...ad, _accion: 'revisar', _motivo: `Ya pausada. ${ctrTxt}. Tiene potencial si mejorás el listing.`, _prioridad })
    } else if (yaPausada) {
      cuadrantes.pozo.push({ ...ad, _accion: 'pausada', _motivo: `Ya pausada. ${ctrTxt}.`, _prioridad: 0 })
    } else if (buenCtr) {
      cuadrantes.pozo.push({ ...ad, _accion: 'pausar-revisar', _motivo: `Pausar. ${ctrTxt}. Buen tráfico — revisá el listing antes de reactivar.`, _prioridad })
    } else {
      cuadrantes.pozo.push({ ...ad, _accion: 'pausar', _motivo: `${ctrTxt}. No atrae visitas al listing.`, _prioridad: 0 })
    }
  }

  // Pérdidas
  for (const ad of sinVentas) {
    const ctr = ad._ctr || 0
    const clicks = ad._clicks || 0
    const precio = ad._precio || 0
    const prints = ad._prints || 0
    const status = ad.status || ''
    const yaPausada = status === 'paused' || status === 'stopped'
    const buenCtr = prints > 0 && ctr >= ctrRef
    const _prioridad = clicks * precio
    const ctrTxt = `CTR ${(ctr * 100).toFixed(2)}% (ref: ${(ctrRef * 100).toFixed(2)}%)`

    let accion
    if (yaPausada && buenCtr) {
      accion = { _accion: 'revisar', _motivo: `Ya pausada. ${ctrTxt}. Atrae gente — revisá el listing.`, _prioridad }
    } else if (yaPausada) {
      accion = { _accion: 'pausada', _motivo: prints === 0 ? 'Ya pausada. Sin impresiones.' : `Ya pausada. ${ctrTxt}.`, _prioridad: 0 }
    } else if (buenCtr) {
      accion = { _accion: 'pausar-revisar', _motivo: `Pausar. ${ctrTxt}. Buen tráfico sin ventas — revisá el listing antes de reactivar.`, _prioridad }
    } else {
      accion = { _accion: 'pausar', _motivo: prints === 0 ? 'Sin impresiones. ML no lo muestra.' : `${ctrTxt}. No atrae visitas.`, _prioridad: 0 }
    }
    cuadrantes.perdida.push({ ...ad, _cuadrante: 'perdida', _acosCalc: null, ...accion })
  }

  const todos = [
    ...cuadrantes.estrella, ...cuadrantes.oportunidad, ...cuadrantes.estable,
    ...cuadrantes.tanque, ...cuadrantes.pozo, ...cuadrantes.perdida,
  ]

  return { acosPromedio, volumenPromedio, ctrRef, cvrRef, cuadrantes, todos }
}

/** Clasificar acción para ads sin ventas (pérdidas puras) */
function clasificarAccionSinVentas(ad, ctrRef) {
  const ctr = ad._ctr || 0
  const clicks = ad._clicks || 0
  const prints = ad._prints || 0
  const precio = ad._precio || 0
  const status = ad.status || ''
  const yaPausada = status === 'paused' || status === 'stopped'
  const ctrTxt = `CTR ${(ctr * 100).toFixed(2)}% (ref: ${(ctrRef * 100).toFixed(2)}%)`
  const _prioridad = clicks * precio

  if (prints === 0) {
    if (yaPausada) return { _accion: 'pausada', _motivo: 'Ya pausada. Sin impresiones.', _prioridad: 0 }
    return { _accion: 'pausar', _motivo: 'Sin impresiones. ML no lo muestra.', _prioridad: 0 }
  }
  if (ctr >= ctrRef) {
    return { _accion: 'revisar', _motivo: `${ctrTxt}. El ad atrae gente — revisá el listing.`, _prioridad }
  }
  if (yaPausada) {
    return { _accion: 'pausada', _motivo: `Ya pausada. ${ctrTxt}.`, _prioridad: 0 }
  }
  return { _accion: 'pausar', _motivo: `${ctrTxt}. No atrae visitas al listing.`, _prioridad: 0 }
}

// ═══════════════════════════════════════════════════════════════
// Clasificador principal — Función pública
// ═══════════════════════════════════════════════════════════════

/**
 * clasificarPublicidad — Clasifica ads de publicidad en secciones accionables
 *
 * @param {Object} datos
 * @param {Array} datos.adsItems - Ads crudos de Firestore (mlAds)
 * @param {Array} datos.publicaciones - Publicaciones (mlPublicaciones)
 * @param {Array} datos.ventas30d - Órdenes de los últimos 30 días
 * @param {Array} datos.campanas - Campañas (mlCampanasAds, para ACOS estratégico)
 * @param {Object} [datos.config] - { acosEstrategico?: number }
 *
 * @returns {Object} { analisis, secciones, kpis, costePausar }
 */
export function clasificarPublicidad({ adsItems, publicaciones, ventas30d, campanas, config, productoBitacora, productos, catalogoLinks, ecosistema = null, impuestosEmpresa = null, categorias = [], marcas = [], proveedores = [], productoSourcing = [], rentabilidadEmpresa = null, ventanaVentasDias = null }) {
  // La ventana con la que el loader cargó `ventas30d` (report
  // djynjlXICuUF0Yl803M8). La usan SÓLO las dos secciones que leen ventas:
  // quebreStock (velocidad = unidades / ventana) y potencial (CVR orgánico =
  // unidades llevadas a 30 días / visitas de 30 días). Hasta este cambio las
  // dos asumían que la ventana era de 30. Sin ventana (decisión de Mariano del
  // 11-oct-2026, pregunta 29: «deja de calcular y avisa») ninguna de las dos se
  // emite: lo que quedó afuera se cuenta y se declara en `lecturas`. Las demás
  // secciones miden métricas de ads, otra fuente, y no cambian.
  const ventanaDias = ventanaDeVentas(ventanaVentasDias)
  const sinVentana = new Set()
  // 15-jul: corte por cascada ADR 032 para el gate de ads de "potencial".
  const infoRentPorItemAds = construirInfoRentabilidadPorItem({
    productos: productos || [], catalogoLinks: catalogoLinks || [], categorias, marcas, proveedores, productoSourcing,
    empresaMin: rentabilidadEmpresa?.rentabilidadMinima ?? null,
  })
  // 1. Enriquecer ads con datos de publicaciones
  const adsEnriquecidos = enriquecerAds(adsItems, publicaciones)

  // 1.5. Cuarentena por condiciones (10-jul-2026) — eventos de la bitácora
  // agrupados por publicación. `config` es el DOC DE LA EMPRESA (lo pasa
  // cargarDatosParaClasificador), así que los umbrales configurables por
  // seller (piso de clicks, umbral de generadores) se leen igual que en el
  // dashboard. Sin bitácora cargada → mapa vacío → sin cuarentena (regla:
  // no inventamos historia).
  const eventosPorItem = new Map()
  for (const evt of (productoBitacora || [])) {
    const ext = evt.publicacionExternalId || evt.publicacionExternalItemId
    if (!ext) continue
    const k = String(ext)
    if (!eventosPorItem.has(k)) eventosPorItem.set(k, [])
    eventosPorItem.get(k).push(evt)
  }
  const cuarentenaDe = (itemId) => {
    if (!itemId) return null
    const evts = eventosPorItem.get(String(itemId))
    if (!evts || evts.length === 0) return null
    return computarCuarentena(evts)
  }
  const pisoClicks = getPisoClicks(config)
  const umbralGenCfg = getUmbralAcosGeneradores(config)

  // 1.6. Mapa itemId → tiempoReposicionDias del producto vinculado (para
  // quebreStock). Mismo patrón que PublicidadDashboard / VelocidadConsumo:
  // items sin link o sin R cargado caen al fallback genérico de 30 días.
  const tiempoReposicionPorItem = new Map()
  {
    const prodById = new Map()
    for (const p of (productos || [])) prodById.set(p.id, p)
    for (const link of (catalogoLinks || [])) {
      if (link.plataformaId && link.plataformaId !== 'mercado-libre') continue
      const prod = prodById.get(link.productoId)
      if (!prod) continue
      const dias = Number(prod.tiempoReposicionDias)
      if (Number.isFinite(dias) && dias > 0) {
        tiempoReposicionPorItem.set(link.externalItemId, { dias, productoId: prod.id })
      }
    }
  }

  // 2. Armar mapa de ventas por item
  const ventasPorItem = buildVentasPorItem(ventas30d)

  // 3. Análisis de eficiencia (cuadrantes)
  const analisis = analizarEficiencia(adsEnriquecidos, campanas, config)
  const q = analisis.cuadrantes

  // 4. Generadores indirectos
  const generadoresIndirectos = [...(q.pozo || []), ...(q.perdida || [])]
    .filter(a => {
      const indirectAmt = a._indirectAmount || 0
      if (indirectAmt === 0) return false
      const cost = a._cost || 0
      return cost > 0 && indirectAmt >= cost * 0.30
    })
    .map(a => {
      const acosConIndirecto = a._amount > 0 ? a._cost / a._amount : null
      const acosSoloDirecto = a._directAmount > 0 ? a._cost / a._directAmount : null
      return {
        ...a,
        _acosConIndirecto: acosConIndirecto,
        _acosSoloDirecto: acosSoloDirecto,
        _contribucionIndirecta: a._indirectAmount || 0,
        _cobertura: a._cost > 0 ? (a._indirectAmount || 0) / a._cost : 0,
      }
    })
    .map(a => {
      const acosT = a._acosConIndirecto
      let _subSenal
      if (acosT != null && acosT <= analisis.acosPromedio) _subSenal = 'alto-rendimiento'
      else if (acosT != null && acosT <= analisis.acosPromedio * 1.30) _subSenal = 'optimizar'
      else _subSenal = 'pausar'
      return { ...a, _subSenal }
    })
    .sort((a, b) => (b._contribucionIndirecta || 0) - (a._contribucionIndirecta || 0))

  // 10-jul-2026 — paridad con el dashboard: solo se PROTEGEN (excluyen de
  // pausar/revisar) los generadores con ACOS TOTAL ≤ umbral configurable
  // (empresas/{id}.umbralesPublicidad.ml.umbralAcosGeneradores; null = ACOS
  // promedio de la cuenta). Antes el agente protegía a TODOS los generadores
  // — incluso los que pierden plata mirando el total.
  const umbralGen = (umbralGenCfg != null) ? umbralGenCfg : analisis.acosPromedio
  const generadoresConProteccion = generadoresIndirectos.map(a => ({
    ...a,
    _protegido: a._acosConIndirecto != null && a._acosConIndirecto <= umbralGen,
  }))
  const idsIndirectos = new Set(generadoresConProteccion.filter(a => a._protegido).map(a => a.id))

  // 5. Secciones
  const buenosAds = [
    ...(q.estrella || []),
    ...(q.oportunidad || []),
    ...(q.estable || []).filter(a => a._acosCalc <= analisis.acosPromedio * 1.10),
  ]

  // Cuarentena por condiciones (10-jul-2026): un ad que se PAUSÓ no se vuelve
  // a sugerir activar hasta que algo material de la publicación cambie después
  // de la pausa (precio/contenido/envío/cuotas/promo — stock NO). Misma regla
  // que la vista manual del dashboard. El agente descarta los en-cuarentena.
  const activarCandidatos = buenosAds
    .filter(a => a.status === 'paused' || a.status === 'stopped')
  const activarEnCuarentena = []
  // Flywheel (14-jul-2026, docs/flywheel-mercadolibre.md): margen neto conocido
  // NEGATIVO → no se reactiva un ad sobre pérdida (CN/pausarRent la trabajan).
  // Sin datos de costo se sugiere igual (declarado).
  let activarAPerdida = 0
  // Flywheel: pub en promo → el ad sacrificado no vuelve mientras la promo
  // viva (cuarentena por CAUSA). Sin stock tampoco se reactiva.
  let activarEnPromo = 0
  let activarSinStock = 0
  const activar = activarCandidatos
    .filter(a => {
      const c = cuarentenaDe(a._itemId || a.id)
      if (c?.enCuarentena) { activarEnCuarentena.push({ ...a, _cuarentena: c }); return false }
      return true
    })
    .filter(a => {
      if (a._margenPct != null && a._margenPct < 0) { activarAPerdida++; return false }
      if (a._enPromo) { activarEnPromo++; return false }
      if (a._stock != null && Number(a._stock) <= 0) { activarSinStock++; return false }
      return true
    })
    .sort((a, b) => b._amount - a._amount)

  const rendimiento = buenosAds.filter(a => esAdOperativo(a.status))

  // Piso de visibilidad (10-jul-2026, paridad dashboard): ads con menos de
  // `pisoClicks` clicks de publicidad NO entran a pausar — todavía no tuvieron
  // exposición suficiente para juzgarlos (configurable por empresa).
  const pausar = [...(q.pozo || []), ...(q.perdida || [])]
    .filter(a => esAdOperativo(a.status) && !idsIndirectos.has(a.id))
    .filter(a => (a._clicks || 0) >= pisoClicks)
    .sort((a, b) => b._cost - a._cost)

  const costePausar = pausar.reduce((s, a) => s + a._cost, 0)

  const estableParaOptimizar = (q.estable || []).filter(a => a._acosCalc > analisis.acosPromedio * 1.10)
  const optimizar = [...(q.tanque || []), ...estableParaOptimizar]
    .filter(a => esAdOperativo(a.status))
    .sort((a, b) => b._cost - a._cost)

  const revisar = [...(q.pozo || []), ...(q.perdida || [])]
    .filter(a => a._accion === 'revisar' && !idsIndirectos.has(a.id))
    .sort((a, b) => (b._prioridad || 0) - (a._prioridad || 0))

  // Potencial: publicaciones SIN ad que venden y convierten bien
  const idsConAd = new Set(adsItems.map(a => a.item_id || a.id?.split('_').pop()))
  const cvrMin = analisis.cvrRef * 0.85

  // Flywheel (14-jul-2026): no se CREA un ad sobre pérdida conocida ni sin stock.
  let potencialAPerdida = 0
  let potencialSinEspacio = 0
  let potencialSinStock = 0
  const potencial = publicaciones
    .filter(pub => {
      if (pub.status !== 'active') return false
      if (idsConAd.has(pub.id)) return false
      // GATE DE ADS (15-jul): margen real − ACOS esperado (cuenta → eco).
      // Proyectado < 0 → veto (antes solo margen < 0). Corte por cascada:
      // pendiente de sumar acá (v2) — el veto a pérdida ya es honesto.
      const margenInf = pub?._rentabilidadInferida?.margenPctProm
      const rentAd = infoRentPorItemAds.get(String(pub.id)) || { costoUnit: null, corte: null }
      const gateAd = gateAdsPrender({
        precio: Number(pub.price),
        margenReal: Number.isFinite(margenInf) ? margenInf : null,
        costoUnit: rentAd.costoUnit, corte: rentAd.corte,
        categoryId: pub?.datosCanal?.categoryId ?? null,
        ecosistema, impuestosEmpresa,
        benchmarksEmpresa: config?._benchmarksML || null,
      })
      if (gateAd.estado === 'veto_perdida') { potencialAPerdida++; return false }
      if (gateAd.estado === 'sin_espacio') { potencialSinEspacio++; return false }
      const stockPub = Number(pub.availableQuantity ?? pub.available_quantity ?? NaN)
      if (Number.isFinite(stockPub) && stockPub <= 0) { potencialSinStock++; return false }
      const ventaVentana = ventasPorItem[pub.id]
      if (!ventaVentana || ventaVentana.uds === 0) return false
      const visitas = pub._visitas?.total_visits ?? pub._visitas?.total ?? 0
      if (visitas === 0) return false
      // Las visitas son de 30 días: las unidades se llevan a 30 días antes de
      // dividir. Sin ventana no hay CVR que comparar.
      if (ventanaDias === null) { sinVentana.add(String(pub.id)); return false }
      const cvr = escalarADias(ventaVentana.uds, ventanaDias, 30) / visitas
      return cvr >= cvrMin
    })
    .map(pub => {
      const visitas = pub._visitas?.total_visits ?? pub._visitas?.total ?? 0
      const ventaVentana = ventasPorItem[pub.id] || { uds: 0, monto: 0 }
      const uds30d = escalarADias(ventaVentana.uds, ventanaDias, 30)
      const monto30d = escalarADias(ventaVentana.monto, ventanaDias, 30)
      const cvr = visitas > 0 ? uds30d / visitas : 0
      return {
        ...pub,
        _visitas30d: visitas,
        _uds30d: uds30d,
        _monto30d: monto30d,
        _cvr: cvr,
        _facOrganica: monto30d,
        _prioridad: monto30d,
      }
    })
    .sort((a, b) => b._prioridad - a._prioridad)

  // ─── pausarRent: ads ACTIVAS de publicaciones con margen neto negativo ───
  // (10-jul-2026 — REGLA Mariano: todo filtro que genera tareas en la vista
  // "🎯 Detección de tareas" tiene que poder correr automático.)
  // Misma lógica que el dashboard: margenPctProm (con publicidad) < 0. Si
  // margenSinPubPct >= 0 la pub es la que hunde; si no, el problema es el
  // producto. Mismo piso de visibilidad que "pausar".
  const pausarRentCandidatos = analisis.todos.filter(a => {
    if (a.status !== 'active') return false
    if (a._margenPct == null) return false
    return a._margenPct < 0
  })
  const pausarRent = pausarRentCandidatos
    .filter(a => (a._clicks || 0) >= pisoClicks)
    .map(a => {
      // La ruta headless persiste el motivo por whitelist explícita
      // (`defaultBuildRecurso` en `CrearAccionableModal.jsx`, `recursoGenericoDe` en `agentes.js`): sólo viaja
      // `_motivo`, así que no se propaga la causa como campo muerto.
      const { motivo } = causaMargenNegativo({
        margenPct: a._margenPct,
        margenSinPubPct: a._margenSinPubPct,
        gastoSinAtribuir: a._gastoSinAtribuir,
      })
      return {
        ...a,
        _accion: 'pausar-rentabilidad',
        _motivo: motivo,
      }
    })
    .sort((a, b) => (a._margenPct || 0) - (b._margenPct || 0)) // peor margen primero

  // ─── quebreStock: ads ACTIVOS de items que quiebran antes de la reposición ───
  // Misma lógica que el dashboard (y que el banner de Velocidad de Consumo):
  // días_a_quiebre = stock / velocidad_total_30d; entra si < tiempoReposicion
  // del producto (o 30 días de fallback si no tiene R cargado). Solo ads que
  // VENDEN (units > 0) — los de gasto sin venta ya van a "pausar".
  const FALLBACK_LEAD_TIME_DIAS = 30
  const quebreStock = adsEnriquecidos
    .filter(a => {
      if (a.status !== 'active') return false
      if (!a._itemId) return false
      if ((a._units || 0) <= 0) return false
      const stock = Number(a._stock || 0)
      if (stock <= 0) return false
      const ventaItem = ventasPorItem[a._itemId]
      if (!ventaItem || ventaItem.uds <= 0) return false
      // Sin ventana, los días a quiebre saldrían de un divisor inventado.
      if (ventanaDias === null) { sinVentana.add(String(a._itemId)); return false }
      const velTotal = ventaItem.uds / ventanaDias
      const diasAQuiebre = stock / velTotal
      const reposInfo = tiempoReposicionPorItem.get(a._itemId) || null
      const umbral = reposInfo?.dias != null ? reposInfo.dias : FALLBACK_LEAD_TIME_DIAS
      return diasAQuiebre < umbral
    })
    .map(a => {
      const stock = Number(a._stock || 0)
      const ventaItem = ventasPorItem[a._itemId] || { uds: 0, monto: 0 }
      const velTotal = ventaItem.uds / ventanaDias
      const diasAQuiebre = velTotal > 0 ? stock / velTotal : Infinity
      const reposInfo = tiempoReposicionPorItem.get(a._itemId) || null
      const tiempoReposicion = reposInfo?.dias || null
      const usaR = tiempoReposicion != null
      const umbral = usaR ? tiempoReposicion : FALLBACK_LEAD_TIME_DIAS
      // El gasto (`_cost`) cubre la ventana de SUS métricas, no la de ventas: se lleva
      // a mes y a «hasta el quiebre» con los días que el mismo doc declara (report
      // bvwlp0MPBf2X9gx58XrL). Sin esa ventana no hay divisor: `null`, nunca un 30.
      const { ahorroMensual, ahorroProyectado } = ahorroDeGastoDeAd(a._cost, a.ventanaMetricasDias, diasAQuiebre)
      const cpa = a._units > 0 ? (a._cost || 0) / a._units : null
      return {
        ...a,
        _velTotal: velTotal,
        _diasAQuiebre: diasAQuiebre,
        _tiempoReposicion: tiempoReposicion,
        _usaR: usaR,
        _umbralAplicado: umbral,
        _cobertura: diasAQuiebre - umbral,
        _ahorroMensual: ahorroMensual,
        _ahorroProyectado: ahorroProyectado,
        _cpa: cpa,
        _ahorroCpaStock: cpa != null ? cpa * stock : null,
        _accion: 'pausar-quiebre',
        _motivo: `Quiebra en ~${Math.max(0, Math.round(diasAQuiebre))} días — stock ${stock}, vende ${velTotal.toFixed(1)}/día${usaR ? `, reposición tarda ${tiempoReposicion} días` : ' (reposición asumida: 30 días)'}.${ahorroProyectado === null ? '' : ` Ahorro estimado si pausás hoy: $${Math.round(ahorroProyectado).toLocaleString('es-AR')}.`}`,
      }
    })
    .sort((a, b) => (a._diasAQuiebre || 0) - (b._diasAQuiebre || 0)) // más urgentes primero

  return {
    // `ventanaDias` se publica junto al análisis de eficiencia (sin mutar el
    // objeto de `analizarEficiencia`): es el descriptor que lee el contrato del
    // registry (ventanaDeVentas.contrato.test.js).
    analisis: { ...analisis, ventanaDias },
    adsEnriquecidos,
    secciones: { activar, rendimiento, pausar, optimizar, revisar, potencial, pausarRent, quebreStock, generadoresIndirectos: generadoresConProteccion },
    // Declarados para logs/telemetría del agente — que el número cierre:
    // cuántos quedaron afuera y por qué (no son secciones accionables).
    excluidos: {
      activarEnCuarentena,
      activarAPerdida,      // flywheel: no se reactiva un ad sobre pérdida
      activarEnPromo,       // flywheel: en promo el ad no vuelve (la promo lo sacrificó)
      activarSinStock,      // flywheel: sin stock no se reactiva
      potencialAPerdida,
      potencialSinEspacio,   // gate ads 15-jul: proyectado abajo de la mínima    // flywheel: no se crea un ad sobre pérdida
      potencialSinStock,    // flywheel: sin stock no se publicita
      sinVentana: sinVentana.size, // quebreStock + potencial sin poder calcular (sin ventana de ventas)
      umbralGeneradoresEfectivo: umbralGen,
      pisoClicks,
    },
    kpis: {
      activar: calcKpis(activar),
      rendimiento: calcKpis(rendimiento),
      pausar: calcKpis(pausar),
      optimizar: calcKpis(optimizar),
      revisar: calcKpis(revisar),
      pausarRent: calcKpis(pausarRent),
      quebreStock: calcKpis(quebreStock),
    },
    costePausar,
    // El canal clasificador → motor (molde: velocidadConsumo), SÓLO si algo
    // quedó sin calcular por la ventana.
    ...(sinVentana.size > 0 ? {
      lecturas: {
        fuentes: [FUENTE_VENTANA],
        degradaciones: [{ fuente: FUENTE_VENTANA, motivo: `sin-dato: no se sabe con cuántos días de ventas se cargaron los datos — ${sinVentana.size} publicación(es) sin evaluar quiebre ni potencial` }],
      },
    } : {}),
  }
}
